import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AceIdentity from '@ace-clublabs/ace-identity';
import { Layout, Loading, ErrorDisplay } from '../components/Layout';
import { fetchAuthConfig, ApiError } from '../services/api';
import { buildRedirectUrl, performRedirect, UserClaims } from '../services/redirect';
import { envConfig } from '../config/env';
import { normalizeClubCode } from '../utils/cookie';
import { logFlowStep, logError, logRedirect } from '../services/logger';

// Session storage keys for persisting state across OAuth redirect
const SESSION_KEYWORD_KEY = 'links_alp_keyword';

const Auth = () => {
  const { keyword: urlKeyword } = useParams<{ keyword: string }>();
  const [status, setStatus] = useState<string>('Initializing...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processAuth = async () => {
      try {
        logFlowStep('auth', 'start');

        // Determine keyword: from URL (initial request) or session storage (post-OAuth redirect)
        let keyword = urlKeyword;

        // Store keyword in session storage for retrieval after OAuth redirect
        if (keyword) {
          sessionStorage.setItem(SESSION_KEYWORD_KEY, keyword);
        } else {
          // Try to retrieve from session storage (post-redirect scenario)
          keyword = sessionStorage.getItem(SESSION_KEYWORD_KEY) || undefined;
        }

        if (!keyword) {
          logError('ValidationError', 'Missing link keyword');
          setError('Missing link keyword. Please check the URL and try again.');
          return;
        }

        setStatus('Setting up authentication...');

        // Initialize AceIdentity
        const aceIdentity = new AceIdentity({
          env: envConfig.aceIdentityEnv,
          redirectUri: envConfig.redirectUri,
          loginUrl: envConfig.loginUrl,
          scopes: ['profile', 'clubCode', 'openid', 'customerId'],
          disableBypassPkce: false,
          bypassAuth2: false,
        });

        logFlowStep('auth', 'ace_identity_init');

        setStatus('Waiting for identity service...');
        await aceIdentity.ready();

        setStatus('Checking authentication status...');

        // Get tokens - this will redirect to login if not authenticated
        const tokens = await aceIdentity.tokens();

        if (!tokens?.accessToken) {
          // tokens() returns undefined while redirecting to login
          logFlowStep('auth', 'redirect_to_login');
          setStatus('Redirecting to login...');
          return;
        }

        logFlowStep('auth', 'tokens_received');
        setStatus('Processing authentication...');

        // Extract claims from ID token
        const idTokenClaims = tokens.idToken ? parseJwt(tokens.idToken) : {};

        const clubCode = idTokenClaims.clubCode;
        if (!clubCode) {
          logError('ValidationError', 'Club code missing from profile');
          setError('Authentication succeeded but club code is missing from your profile.');
          return;
        }

        const normalizedClubCode = normalizeClubCode(clubCode);

        const claims: UserClaims = {
          clubCode: normalizedClubCode,
          memberNumber: idTokenClaims.memberNumber,
          customerId: idTokenClaims.customerId,
        };

        setStatus('Fetching redirect configuration...');
        logFlowStep('auth', 'fetch_config', { keyword });

        // Fetch config from authenticated API endpoint
        const config = await fetchAuthConfig(keyword, tokens.accessToken);

        logFlowStep('auth', 'config_received', { keyword, alpid: config.alpid, clubCode: normalizedClubCode });
        setStatus('Redirecting...');

        // Clear session storage
        sessionStorage.removeItem(SESSION_KEYWORD_KEY);

        // Build and perform redirect
        const redirectUrl = buildRedirectUrl(config, claims);
        logRedirect(keyword, config.alpid, normalizedClubCode);
        performRedirect(redirectUrl);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        logError('AuthRedirectError', errorMessage);

        if (err instanceof ApiError) {
          if (err.isNotFound) {
            setError('Link not found. Please check the URL and try again.');
          } else if (err.isForbidden) {
            setError('You do not have permission to access this link.');
          } else {
            setError(err.message || 'An error occurred');
          }
        } else {
          setError(errorMessage);
        }
      }
    };

    processAuth();
  }, [urlKeyword]);

  return (
    <Layout>
      {error ? (
        <ErrorDisplay message={error} onRetry={() => window.location.reload()} />
      ) : (
        <Loading message={status} />
      )}
    </Layout>
  );
};

/**
 * Simple JWT parser to extract claims from ID token
 * Note: This does NOT validate the token - validation is done server-side
 */
function parseJwt(token: string): Record<string, any> {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

export default Auth;
